import React from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { GiSoccerBall } from "react-icons/gi";
import config from "../../../config/config";

export const GoalScoreCard = ({ data }) => {
  const { i18n, t } = useTranslation();
  const groupedData = data.reduce((groups, match) => {
    const league = match?.league || "—";
    if (!groups[league]) groups[league] = [];
    groups[league].push(match);
    return groups;
  }, {});

  return (
    <div className="space-y-3">
      {Object.entries(groupedData).map(([league, matches]) => (
        <section
          key={league}
          className="overflow-hidden rounded-lg border border-primary/40 bg-header_green/55 shadow-lg shadow-black/15 backdrop-blur-sm"
        >
          <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-black/10 px-3.5 py-2.5">
            <div className="flex min-w-0 items-center gap-2">
              <GiSoccerBall className="h-4 w-4 flex-none text-emerald-200" />
              <h2 className="truncate text-xs font-semibold leading-[1.8] text-white">
                {league}
              </h2>
            </div>
            <span className="flex-none font-sans text-[9px] uppercase tracking-wider text-white/40">
              {matches.length} {t("matches")}
            </span>
          </div>

          <div className="divide-y divide-white/10">
            {matches.map((event, index) => {
              const homeGoal = event?.homeGoal === "NE" ? "—" : event?.homeGoal;
              const awayGoal = event?.awayGoal === "NE" ? "—" : event?.awayGoal;
              const homeWins = Number(homeGoal) > Number(awayGoal);
              const awayWins = Number(awayGoal) > Number(homeGoal);
              const status = config.goal_status[event?.status] || event?.status || "—";

              return (
                <article
                  key={event?.eventId || `${event?.event}-${index}`}
                  className="grid grid-cols-[64px_minmax(0,1fr)_36px] items-center gap-3 px-3.5 py-2.5"
                >
                  <div className="border-r border-white/10 pr-3 text-center font-sans">
                    <p className="flex items-center justify-center gap-1 text-[10px] text-white/55">
                      <FiClock className="h-3 w-3" />
                      {moment(event?.event).format("h:mm A")}
                    </p>
                    <span className={`mt-1 inline-flex rounded-md px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wide ${
                      event?.status === "FT"
                        ? "bg-white/10 text-white/45"
                        : "bg-emerald-300/15 text-emerald-200"
                    }`}>
                      {status}
                    </span>
                  </div>

                  <div className="min-w-0 space-y-1.5">
                    <TeamName
                      name={i18n.language === "en" ? event?.homeTeam : event?.homeTeamMyan || event?.homeTeam}
                      emphasized={homeWins}
                    />
                    <TeamName
                      name={i18n.language === "en" ? event?.awayTeam : event?.awayTeamMyan || event?.awayTeam}
                      emphasized={awayWins}
                    />
                  </div>

                  <div className="space-y-1.5 font-sans">
                    <Score value={homeGoal} emphasized={homeWins} />
                    <Score value={awayGoal} emphasized={awayWins} />
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

const TeamName = ({ name, emphasized }) => (
  <p className={`truncate text-xs leading-[1.8] ${emphasized ? "font-semibold text-white" : "text-white/65"}`}>
    {name || "—"}
  </p>
);

const Score = ({ value, emphasized }) => (
  <span className={`flex h-7 w-8 items-center justify-center rounded-md text-xs font-bold ${
    emphasized ? "bg-white text-header_green" : "bg-white/10 text-white/65"
  }`}>
    {value ?? "—"}
  </span>
);
